'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { CONTENT } from '../content/landing'
import { useIsDesktop } from '../hooks/useMediaQuery.js'
import './landing.css'
import ClosingCta from './sections/ClosingCta.jsx'
import Faq from './sections/Faq.jsx'
import Features from './sections/Features.jsx'
import Hero from './sections/Hero.jsx'
import HowItWorks from './sections/HowItWorks.jsx'
import Scholarships from './sections/Scholarships.jsx'
import SiteFooter from './sections/SiteFooter.jsx'
import SiteHeader from './sections/SiteHeader.jsx'
import TrustStrip from './sections/TrustStrip.jsx'

const DEFAULT_LANG = 'uz'
const LOCALE_COOKIE = 'NEXT_LOCALE'

/**
 * Uniap.uz v2 marketing landing page.
 *
 * Grown out of `Landing Page.html` / `Landing.jsx` from the Claude Design
 * project: same design system, tokens and copy, restructured around a centred
 * hero with a still of the cabinet, a programme strip, a feature grid and a
 * three-column footer. The design chose its layout from a `desktop` boolean;
 * that boolean now comes from `matchMedia` at the same 720px breakpoint, so it
 * follows resizes instead of freezing at first paint.
 *
 * Section order — hero, programmes, features, steps, scholarships, FAQ,
 * closing CTA — is deliberate: what it is, what is in it, what it does, how
 * to start, proof, then the ask.
 *
 * `authHref`, `signedIn` and `banner` come from the server page (T-WEB-09):
 * the CTA target carries the locale and post-login destination, a signed-in
 * visitor gets "Open cabinet" instead of "Start free", and a failed OAuth
 * round trip renders its banner above the hero.
 *
 * `initialLang` comes from the `/[locale]` route segment (T-WEB-03). Language
 * switching navigates to the sibling locale route via `next/navigation` and
 * sets the `NEXT_LOCALE` cookie next-intl's middleware reads on the next
 * request, instead of only flipping local state.
 *
 * @param {{
 *   initialLang?: string,
 *   authHref?: string,
 *   signedIn?: boolean,
 *   banner?: import('react').ReactNode,
 * }} props
 */
export function Landing({ initialLang = DEFAULT_LANG, authHref, signedIn = false, banner = null }) {
  const desktop = useIsDesktop()
  const [lang, setLang] = useState(initialLang)
  const router = useRouter()
  const t = CONTENT[lang]

  // The route (and therefore `initialLang`) changes on every language switch;
  // keep local state in sync so a parent re-render after navigation is
  // reflected immediately, without waiting on the `document.documentElement`
  // effect below.
  useEffect(() => {
    setLang(initialLang)
  }, [initialLang])

  // Keep the document language in step so screen readers pick the right voice.
  // The root layout also sets `<html lang>` from the route param on first
  // load; this effect is the guard for client-side locale transitions.
  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const handleLangChange = (l) => {
    document.cookie = `${LOCALE_COOKIE}=${l}; path=/; max-age=31536000`
    router.replace('/' + l)
  }

  return (
    <div id="top" className="u-page" style={{ background: 'var(--canvas)', minHeight: '100%' }}>
      <a className="u-skip" href="#main">
        {t.skip}
      </a>

      <SiteHeader
        t={t}
        lang={lang}
        onLangChange={handleLangChange}
        desktop={desktop}
        authHref={authHref}
        signedIn={signedIn}
      />

      <main id="main">
        {banner}
        <Hero t={t} desktop={desktop} authHref={authHref} signedIn={signedIn} />
        <TrustStrip t={t} desktop={desktop} />
        <Features t={t} desktop={desktop} />
        <HowItWorks t={t} desktop={desktop} />
        <Scholarships t={t} desktop={desktop} authHref={authHref} signedIn={signedIn} />
        <Faq t={t} desktop={desktop} />
        <ClosingCta t={t} desktop={desktop} authHref={authHref} signedIn={signedIn} />
      </main>

      <SiteFooter t={t} desktop={desktop} lang={lang} />
    </div>
  )
}

export default Landing
